import { Plus, Tag } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCreateCustomerClassification,
	useCustomerClassifications,
	useDeleteCustomerClassification,
	useUpdateCustomerClassification,
} from '@/api/queries/customer-classifications-queries'
import { CustomerClassificationFormContent } from '@/components/administration'
import {
	AdminCardPage,
	CardListItem,
	ResponsiveFormModal,
	SearchInput,
} from '@/components/commons'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import type {
	CustomerClassificationInput,
	CustomerClassificationOutput,
} from '@/types/customer-classification'
import { getApiErrorMessage } from '@/utils/api-error'

export const CustomerClassificationsPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()

	const [search, setSearch] = useState('')
	const [formOpen, setFormOpen] = useState(false)
	const [editing, setEditing] = useState<
		CustomerClassificationOutput | undefined
	>(undefined)
	const [deleteTarget, setDeleteTarget] = useState<
		CustomerClassificationOutput | undefined
	>(undefined)

	const { data, isLoading } = useCustomerClassifications({
		page: 1,
		limit: 100,
	})
	const createMutation = useCreateCustomerClassification()
	const updateMutation = useUpdateCustomerClassification()
	const deleteMutation = useDeleteCustomerClassification()

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase()
		const items = data?.items ?? []
		return q
			? items.filter((item) => item.name.toLowerCase().includes(q))
			: items
	}, [data?.items, search])

	const total = filtered.length

	const handleOpenCreate = () => {
		setEditing(undefined)
		setFormOpen(true)
	}

	const handleOpenEdit = (item: CustomerClassificationOutput) => {
		setEditing(item)
		setFormOpen(true)
	}

	const handleSave = async (values: CustomerClassificationInput) => {
		try {
			if (editing) {
				await updateMutation.mutateAsync({ id: editing.id, body: values })
				toast.success(
					t('administration.customerClassifications.editSuccess', {
						name: values.name,
					}),
				)
			} else {
				await createMutation.mutateAsync(values)
				toast.success(
					t('administration.customerClassifications.createSuccess', {
						name: values.name,
					}),
				)
			}
			setFormOpen(false)
			setEditing(undefined)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const handleDeleteConfirm = async () => {
		if (!deleteTarget) return
		try {
			await deleteMutation.mutateAsync(deleteTarget.id)
			toast.success(
				t('administration.customerClassifications.deleteSuccess', {
					name: deleteTarget.name,
				}),
			)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
		setDeleteTarget(undefined)
	}

	const formTitle = editing
		? t('administration.customerClassifications.editClassification')
		: t('administration.customerClassifications.createNew')

	return (
		<>
			<AdminCardPage
				icon={<Tag className="size-5" />}
				title={t('administration.customerClassifications.title')}
				subtitle={t('administration.customerClassifications.subtitle')}
				onBack={() =>
					navigate(
						adminBackRoute(ROUTES.ADMINISTRATION.CUSTOMER_CLASSIFICATIONS),
					)
				}
				footerText={`${total} ${
					total === 1
						? t('administration.customerClassifications.countSingular')
						: t('administration.customerClassifications.countPlural')
				}`}
				actions={
					<Button
						onClick={handleOpenCreate}
						className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
					>
						<Plus className="size-4" />
						<span>{t('administration.customerClassifications.createNew')}</span>
					</Button>
				}
				modal={
					<ResponsiveFormModal
						open={formOpen}
						onOpenChange={setFormOpen}
						title={formTitle}
						dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-lg"
					>
						<CustomerClassificationFormContent
							key={editing?.id ?? 'new'}
							mode={editing ? 'edit' : 'create'}
							initial={editing}
							onSave={handleSave}
							onClose={() => setFormOpen(false)}
						/>
					</ResponsiveFormModal>
				}
			>
				<SearchInput
					value={search}
					onChange={setSearch}
					placeholder={t(
						'administration.customerClassifications.searchPlaceholder',
					)}
				/>

				{isLoading ? (
					<div className="p-6 text-center text-sm text-muted-foreground">
						{t('administration.customerClassifications.loading')}
					</div>
				) : filtered.length === 0 ? (
					<div className="flex flex-col items-center gap-2 py-16 text-center text-muted-foreground">
						<Tag className="size-8 text-kaefer-light-silver" />
						<p className="text-sm">
							{t('administration.customerClassifications.noResults')}
						</p>
					</div>
				) : (
					<div className="space-y-2">
						{filtered.map((item) => (
							<CardListItem
								key={item.id}
								title={item.name}
								onEdit={() => handleOpenEdit(item)}
								onDelete={() => setDeleteTarget(item)}
							/>
						))}
					</div>
				)}
			</AdminCardPage>

			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(open) => !open && setDeleteTarget(undefined)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('administration.customerClassifications.deleteTitle')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('administration.customerClassifications.deleteDescription', {
								name: deleteTarget?.name ?? '',
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t('administration.customerClassifications.deleteCancelButton')}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDeleteConfirm}
							className="bg-red-600 text-white hover:bg-red-700"
						>
							{t('administration.customerClassifications.deleteConfirmButton')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
